import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import { getMovieById, type Movie } from '@/mocks/movies';
import { getArticleById, type Article } from '@/mocks/articles';

type ContentType = 'movie' | 'article' | 'notfound';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [contentType, setContentType] = useState<ContentType>('notfound');
  const [movie, setMovie] = useState<Movie | null>(null);
  const [article, setArticle] = useState<Article | null>(null);

  useEffect(() => {
    const numId = parseInt(id || '0', 10);

    // Check if it's a movie (IDs 1-100 reserved for movies)
    const foundMovie = getMovieById(numId);
    if (foundMovie) {
      setMovie(foundMovie);
      setArticle(null);
      setContentType('movie');
      return;
    }

    // Check if it's an article
    const foundArticle = getArticleById(numId);
    if (foundArticle) {
      setArticle(foundArticle);
      setMovie(null);
      setContentType('article');
      return;
    }

    setContentType('notfound');
  }, [id]);

  if (contentType === 'notfound') {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-14">
          <div className="max-w-[800px] mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-300"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">内容未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">抱歉，您要查看的内容不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line mr-2"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (contentType === 'movie' && movie) {
    return (
      <>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Movie',
            name: movie.title,
            description: movie.description,
            image: movie.image,
            director: { '@type': 'Person', name: movie.director },
            actor: movie.cast.map((c) => ({ '@type': 'Person', name: c })),
            dateCreated: `${movie.year}`,
            duration: movie.duration,
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: movie.rating,
              bestRating: '10',
              ratingCount: Math.floor(Math.random() * 500000) + 10000,
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.aiyaaiya.com/' },
              { '@type': 'ListItem', position: 2, name: '电影详情', item: `https://www.aiyaaiya.com/article/${movie.id}` },
            ],
          })}
        </script>

        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main>
            {/* Hero Banner */}
            <section className="relative w-full pt-16">
              <div className="relative h-[300px] md:h-[400px] overflow-hidden">
                <img
                  src={movie.image}
                  alt={movie.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-background-50"></div>
              </div>

              <div className="max-w-[1280px] mx-auto px-4 md:px-6 relative -mt-32 md:-mt-40">
                {/* Breadcrumb */}
                <nav className="flex items-center gap-2 text-xs text-white/80 mb-4" aria-label="面包屑导航">
                  <Link to="/" className="hover:text-white transition-colors cursor-pointer">首页</Link>
                  <span>/</span>
                  <span className="text-white/60">电影详情</span>
                </nav>

                <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                  {/* Poster */}
                  <div className="w-40 md:w-56 flex-shrink-0">
                    <div className="aspect-[2/3] rounded-xl overflow-hidden">
                      <img
                        src={movie.image}
                        alt={movie.title}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>

                  {/* Info */}
                  <div className="flex-1 pt-2">
                    <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">
                      {movie.title}
                    </h1>
                    <p className="text-sm text-foreground-500 mb-4">{movie.subtitle}</p>

                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <span className="px-3 py-1 bg-accent-100/80 text-accent-700 text-sm font-semibold rounded-lg">
                        <i className="ri-star-fill mr-1"></i>
                        {movie.rating}
                      </span>
                      <span className="text-sm text-foreground-500">{movie.year}</span>
                      <span className="text-sm text-foreground-400">·</span>
                      <span className="text-sm text-foreground-500">{movie.duration}</span>
                      <span className="text-sm text-foreground-400">·</span>
                      <span className="text-sm text-foreground-500">{movie.region}</span>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {movie.tags.map((tag) => (
                        <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap">
                          {tag}
                        </span>
                      ))}
                    </div>

                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">{movie.description}</p>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-foreground-400">导演：</span>
                        <span className="text-foreground-700">{movie.director}</span>
                      </div>
                      <div>
                        <span className="text-foreground-400">主演：</span>
                        <span className="text-foreground-700">{movie.cast.join('、')}</span>
                      </div>
                    </div>

                    <button className="mt-6 px-8 py-3 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap">
                      <i className="ri-play-circle-line mr-2"></i>
                      立即播放
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {/* Related Movies */}
            <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
              <h2 className="text-lg font-heading font-bold text-foreground-900 mb-6">相关推荐</h2>
              <p className="text-sm text-foreground-500">更多精彩影片等您来发现</p>
            </section>
          </main>
          <Footer />
          <ScrollToTop />
        </div>
      </>
    );
  }

  if (contentType === 'article' && article) {
    return (
      <>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.summary,
            image: article.image,
            author: { '@type': 'Person', name: article.author },
            datePublished: article.date,
            publisher: {
              '@type': 'Organization',
              name: '爱丫爱丫影院',
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.aiyaaiya.com/' },
              { '@type': 'ListItem', position: 2, name: '影视资讯', item: `https://www.aiyaaiya.com/article/${article.id}` },
            ],
          })}
        </script>

        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main>
            <article className="max-w-[800px] mx-auto px-4 md:px-6 pt-24 pb-14">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
                <span>/</span>
                <Link to="/article/1" className="hover:text-primary-500 transition-colors cursor-pointer">影视资讯</Link>
                <span>/</span>
                <span className="text-foreground-600">{article.title}</span>
              </nav>

              {/* Header */}
              <header className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full whitespace-nowrap">
                    {article.category}
                  </span>
                  <span className="text-xs text-foreground-400">{article.date}</span>
                </div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3 leading-tight">
                  {article.title}
                </h1>
                <p className="text-base text-foreground-500 leading-relaxed">{article.summary}</p>
                <div className="flex items-center gap-4 mt-4 pt-4 border-t border-background-200">
                  <span className="text-sm text-foreground-500">
                    <i className="ri-user-line mr-1"></i>
                    {article.author}
                  </span>
                  <span className="text-sm text-foreground-400">
                    <i className="ri-eye-line mr-1"></i>
                    {article.readCount.toLocaleString()} 阅读
                  </span>
                </div>
              </header>

              {/* Featured Image */}
              <div className="aspect-[16/9] rounded-xl overflow-hidden mb-8">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Content */}
              <div
                className="prose prose-sm max-w-none text-foreground-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap">
                    #{tag}
                  </span>
                ))}
              </div>
            </article>
          </main>
          <Footer />
          <ScrollToTop />
        </div>
      </>
    );
  }

  return null;
}